/**
 * SheetCard Component
 *
 * Props:
 *
 * @prop {string}   color                - สีพื้นหลังของการ์ด (hex string เช่น "#B2BB1F")
 *                                         ถ้าไม่ส่งมาจะใช้ค่า default "#B2BB1F"
 *
 * @prop {object}   sheet                - ข้อมูลชีทที่จะแสดงผล ประกอบด้วย:
 *   @prop {string}   sheet._id          - ID ของชีท ใช้สำหรับ redirect และ API calls
 *   @prop {string}   sheet.name         - ชื่อวิชา จะถูก split เป็น รหัสวิชา (code) และชื่อวิชา (name)
 *   @prop {boolean}  sheet.highlighted  - ถ้าเป็น true จะแสดง badge "ชีทแนะนำ!" ด้านบนการ์ด
 *   @prop {number}   sheet.category     - หมวดหมู่ของชีท (0=ทั้งเทอม, 1=มิดเทอม, 2=ไฟนอล,
 *                                         3=สอบในห้อง, 4=อ่านเข้ามหาวิทยาลัย, 5=อ่านเข้า ม.4)
 *   @prop {number}   sheet.semester     - เทอมที่ของชีท
 *   @prop {number}   sheet.year         - ปีการศึกษาของชีท
 *   @prop {number}   sheet.bought       - จำนวนที่มีคนซื้อแล้ว (แสดงในโหมดยังไม่ซื้อ)
 *   @prop {string}   sheet.authorCode   - รหัสผู้เขียน/เจ้าของชีท
 *   @prop {number}   sheet.price        - ราคาชีท (แสดงบนปุ่มซื้อ)
 *   @prop {object}   sheet.onlineTutor  - ข้อมูลการติวออนไลน์ (ถ้าไม่มีให้ส่ง null/undefined)
 *     @prop {boolean}  onlineTutor.isLiveSteamEnded  - true = live จบแล้ว (แสดงปุ่มดูย้อนหลัง)
 *     @prop {string}   onlineTutor.liveReplayUrl     - URL คลิปย้อนหลัง
 *     @prop {string}   onlineTutor.url               - URL เข้าห้อง live
 *     @prop {number}   onlineTutor.startDate         - Unix timestamp เวลาเริ่ม live
 *
 * @prop {number}   index                - ลำดับของการ์ดในลิสต์ ใช้เลือก pattern ลายพื้นหลัง (0–7 วนซ้ำ)
 *
 * @prop {string}   size                 - ความกว้างของการ์ด (CSS value เช่น "260px") default = "260px"
 *
 * @prop {boolean}  bought               - true = ผู้ใช้ซื้อชีทนี้แล้ว → แสดงปุ่ม "อ่านชีท" และ dropdown
 *                                         false/undefined = ยังไม่ซื้อ → แสดงปุ่มใส่ตะกร้าพร้อมราคา
 *
 * @prop {boolean}  hiddenCardButton     - true = ซ่อนปุ่มใส่ตะกร้า (ใช้เมื่ออยู่ในโหมดยังไม่ซื้อ)
 *
 * @prop {function} addCart              - callback เมื่อกดปุ่มใส่ตะกร้า รับ (sheetId: string)
 *
 * @prop {boolean}  isDraftSheet         - true = ชีทฉบับร่าง
 *
 * @prop {function} callbackClick        - callback เมื่อคลิกการ์ด ถ้าไม่ส่งจะ redirect ไปหน้า /infoSheet/:id
 *
 * @prop {function} callbackEditBtnClick - callback เมื่อคลิกปุ่มแก้ไข (ใช้ในโหมดชีทฉบับร่าง)
 *
 *  @prop {function} callbackSendToSystemSuccess - callback เมื่อส่งชีทเข้าสู่ระบบสำเร็จ
 */

// ─── Imports ────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import { Dropdown, Tooltip } from "antd";
import style from "./Style.module.css";
import "./sheetCard.css";
import Pattern1 from "./Pattern/Pattern1";
import Pattern2 from "./Pattern/Pattern2";
import Pattern3 from "./Pattern/Pattern3";
import Pattern4 from "./Pattern/Pattern4";
import Pattern5 from "./Pattern/Pattern5";
import Pattern6 from "./Pattern/Pattern6";
import Pattern7 from "./Pattern/Pattern7";
import Pattern8 from "./Pattern/Pattern8";
import Spinner from "./Spinner";
import Suggested from "../../asset/suggestedSheet.svg";
import PenIcon from "../../asset/pen.svg";
import { ReactComponent as SteamIcon } from "../../asset/steamIcon.svg";
import TripleDotIcon from "../../asset/TripleDotIcon";
import CartSheet from "../../asset/CartSheet";
import DocumentIcon from "../../asset/sheetCardIcon/Document";
import CommentIcon from "../../asset/sheetCardIcon/Comment";
import VideoOnlineIcon from "../../asset/sheetCardIcon/VideoOnline";
import ReportIcon from "../../asset/sheetCardIcon/Report";
import AttendTheEvent from "../../asset/sheetCardIcon/AttendTheEvent";
import ExportFileIcon from "../../asset/sheetCardIcon/ExportFile";
import InformationIcon from "../../asset/sheetCardIcon/information";
// import TrashIcon from "../../asset/sheetCardIcon/Trash";

import Util from "../../Util";
import ShowReview from "../../Components/ShowReview";
import ShowReport from "../../Components/ShowReport";
import TermAndConditionSellerModal from "../../Pages/EditSheet/TermAndConditionSellerModal";

// ─── Constants ───────────────────────────────────────────────────────────────

const PATTERN_COMPONENTS = [
  Pattern1,
  Pattern2,
  Pattern3,
  Pattern4,
  Pattern5,
  Pattern6,
  Pattern7,
  Pattern8,
];

const CATEGORY_LABELS = [
  "ทั้งเทอม",
  "มิดเทอม",
  "ไฟนอล",
  "สอบในห้อง",
  "อ่านเข้ามหาวิทยาลัย",
  "อ่านเข้า ม.4",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatTime = (timestamp) => {
  const date = new Date(timestamp * 1000);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const formatDateTimestamp = (timestamp) => {
  const date = new Date(timestamp * 1000);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year} | ${formatTime(timestamp)}`;
};

// ─── Component ───────────────────────────────────────────────────────────────

const SheetCard = (props) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);

  // ── State ──────────────────────────────────────────────────────────────────

  const [shouldScroll, setShouldScroll] = useState(false);
  const [isHoveredDropdown, setIsHoveredDropdown] = useState(false);
  const [isLight, setIsLight] = useState(false);
  const [fontColor, setFontColor] = useState("#ffffff");
  const [code, setCode] = useState();
  const [name, setName] = useState();
  const [progress, setProgress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progressReview, setProgressReview] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [termAndConditionModal, setTermAndConditionModal] = useState(false);

  const [review, setReview] = useState({
    reviewID: "",
    reviewName: "",
    review: { before: false, score: 0, message: "" },
  });
  const [report, setReport] = useState({
    reportID: "",
    reportName: "",
    report: { category: "ชีทอ่านไม่รู้เรื่อง", message: "" },
  });

  // ── Derived Values ─────────────────────────────────────────────────────────

  const bgColor = props?.color ?? "#B2BB1F";
  const darkenPropColor = Util.darkenColor(bgColor);
  const bgButtonColor = isLight ? "#494949" : "#FFF";
  const isSuggested = props.sheet?.highlighted;
  const isTutoring = props.sheet?.onlineTutor != null;
  const isExpired = props.sheet?.onlineTutor?.isLiveSteamEnded ?? false;
  const Pattern =
    PATTERN_COMPONENTS[(props?.index ?? 0) % PATTERN_COMPONENTS.length];

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!props?.color) return;
    const light = Util.checkColorLight(props.color);
    setIsLight(light);
    setFontColor(light ? "#000000" : "#ffffff");
  }, [props.color]);

  useEffect(() => {
    const { code, name } = Util.splitTitle(props?.sheet?.name ?? "");
    setCode(code);
    setName(name);
  }, [props.sheet?.name]);

  useEffect(() => {
    if (!code) return;
    const container = containerRef.current;
    const textEl = textRef.current;
    if (container && textEl) {
      setShouldScroll(textEl.scrollWidth > container.clientWidth);
    }
  }, [code]);

  // ── API Handlers ───────────────────────────────────────────────────────────

  const getReviewData = async (sheet) => {
    const id = sheet._id;
    setProgressReview(true);
    const data = await Util.sendPost("/api/user/getMyScore", { sheet: id });
    setProgressReview(false);
    if (!data.success) {
      alert("Error, please try again");
      return;
    }
    setReview({
      reviewID: id,
      reviewName: sheet.name,
      review: data.data.score
        ? { before: true, message: data.data.score.message }
        : { before: false, message: "", score: 0 },
    });
    setShowReview(true);
  };

  const setReportData = (sheet) => {
    setReport({
      reportID: sheet._id,
      reportName: sheet.name,
      report: { message: "", category: "ชีทอ่านไม่รู้เรื่อง" },
    });
    setShowReport(true);
  };

  const downloadSheet = async () => {
    try {
      setDownloadProgress(true);
      const data = await Util.sendPost("/api/user/readMySheet", {
        id: props.sheet._id,
      });
      if (data.success) {
        const link = document.createElement("a");
        link.href = data.data;
        link.download = `${props.sheet.name}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(data.data);
      }
    } finally {
      setDownloadProgress(false);
    }
  };

  const onClickYes = async () => {
    const data = await Util.sendPost("/api/seller/confirmSend", {
      id: props.sheet._id,
      message: props.sheet.message ?? "",
    });

    if (!data.success) {
      Util.alertError(data.msg);
      return;
    }
    Util.alertSuccess(
      "ส่งชีทเข้าสู่การตรวจสอบสำเร็จ สามารถติดตามสถานะได้ที่หน้าขายชีท",
      async () => {
        setSaving(false);
        if (props.callbackSendToSystemSuccess)
          props.callbackSendToSystemSuccess();
        this.props.history.push("/seller/mysheet");
      },
    );
  };

  const confirmSend = async () => {
    if (
      props?.sheet?.store !== 999999 &&
      props?.sheet?.store !== 9997 &&
      props?.sheet?.store !== 32 &&
      props?.sheet?.classInfo.trim().length === 0
    ) {
      Util.alertError("กรุณากรอกข้อมูลคลาส/ตอนเรียน");
      return;
    }
    if (
      props?.sheet?.store !== 999999 &&
      props?.sheet?.store !== 9997 &&
      props?.sheet?.store !== 32 &&
      props?.sheet?.detail.trim().length === 0
    ) {
      Util.alertError("กรุณากรอกข้อมูลรายละเอียดเนื้อหา");
      return;
    }
    // if (!/^\d.*$/.test(props?.sheet?.price + "")) {
    //   Util.alertError("ราคาไม่เป็นตัวเลข");
    //   return;
    // }
    if (typeof props?.sheet?.price !== "number") {
      Util.alertError("ราคาไม่เป็นตัวเลข");
      return;
    }

    if (props?.sheet?.name.trim().length === 0) {
      Util.alertError(
        "โปรดกรอกข้อมูลรหัสวิชา หรือชื่อวิชา อย่างน้อยอย่างใดอย่างหนึ่ง",
      );
      return;
    }
    onClickYes();
  };

  // ── Dropdown Menu Items ────────────────────────────────────────────────────

  const dropdownItems = [
    {
      key: "item-review",
      label: (
        <div
          className={style.dropdownItem}
          onClick={(e) => {
            e.stopPropagation();
            getReviewData(props.sheet);
          }}
        >
          {progressReview ? (
            <Spinner color={"#ffcd09"} size={20} />
          ) : (
            <CommentIcon />
          )}
          <span className={style.labelItem}>เขียน/แก้ไข คอมเมนต์</span>
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "item-report",
      label: (
        <div
          className={style.dropdownDangerousItem}
          onClick={(e) => {
            e.stopPropagation();
            setReportData(props.sheet);
          }}
        >
          <ReportIcon />
          <span className={style.labelDangerousItem}>รายงาน</span>
        </div>
      ),
    },
  ];

  // ── Render Helpers ─────────────────────────────────────────────────────────

  const renderCourseCode = () => {
    const codeStyle = {
      fontWeight: 400,
      "--marquee-speed": "50px",
      color: code
        ? fontColor
        : isLight
          ? fontColor
          : "rgba(255, 255, 255, 0.30)",
    };
    return (
      <h1
        ref={textRef}
        style={codeStyle}
        className={`marquee-text ${shouldScroll ? "scroll" : ""}`}
      >
        {code || "รหัสยังไม่ถูกระบุ"}
      </h1>
    );
  };

  const renderCourseName = () => {
    if (name) {
      return (
        <Tooltip title={name}>
          <h4 className={style.nameText} style={{ color: fontColor }}>
            {name}
          </h4>
        </Tooltip>
      );
    }
    return (
      <h4 className={style.nameTextOpacity} style={{ color: fontColor }}>
        (ชื่อรายวิชายังไม่ถูกระบุ)
      </h4>
    );
  };

  const renderDraftStatusTag = () => {
    if (!props.isDraftSheet) return null;
    if (props.sheet?.status === 0) {
      return (
        <div className={style.draftStatusTag} style={{ color: "#FFD019" }}>
          ร่าง
        </div>
      );
    }
    if (props.sheet?.status === 1) {
      return (
        <div className={style.draftStatusTag} style={{ color: "#1889FA" }}>
          กำลังตรวจสอบความถูกต้อง
        </div>
      );
    }
    if (props.sheet?.status === 3) {
      return (
        <Tooltip title={props.sheet?.reason ?? ""}>
          <div className={style.draftStatusTag} style={{ color: "#F51919" }}>
            <div>ไม่ผ่านเกณฑ์</div>
            <InformationIcon color="#F51919" />
          </div>
        </Tooltip>
      );
    }
    return null;
  };

  const renderTopLabel = () => {
    return (
      <div className={style.topLabel} style={{ color: fontColor }}>
        <p>
          {props?.sheet?.category !== undefined
            ? CATEGORY_LABELS[props?.sheet?.category] + " | "
            : "-"}
          {props?.sheet?.semester !== undefined &&
            props?.sheet?.year !== undefined && (
              <>
                เทอม {props?.sheet?.semester}/{props?.sheet?.year}
              </>
            )}
        </p>
        {!props?.bought && !props.isDraftSheet ? (
          <p>
            <span
              className={isLight ? style.darkGrayText : style.lightGrayText}
            >
              ซื้อแล้ว
            </span>{" "}
            {props?.sheet?.bought} ชุด
          </p>
        ) : (
          ""
        )}
      </div>
    );
  };

  const renderOnlineTutorTag = () => {
    const tutor = props.sheet?.onlineTutor;
    if (!tutor) return null;
    if (tutor.isLiveSteamEnded) {
      return (
        <div className={style.tagSteamEnded}>
          <SteamIcon />
          <span>ชีทพร้อมคลิปติวย้อนหลัง</span>
        </div>
      );
    }
    return (
      <div className={style.tagTutoring}>
        <div className={style.blackGradientTag}>ติวออนไลน์</div>
        <p style={{ margin: 0 }} className={style.timeTutoring}>
          {formatDateTimestamp(tutor.startDate)}
        </p>
      </div>
    );
  };

  const renderReadSheetBtn = () => (
    <button
      className={style.readSheetBtn}
      style={{
        color: bgColor,
        backgroundColor: bgButtonColor,
        borderColor: isLight ? "#494949" : "#ccc",
      }}
      onClick={(e) => {
        e.stopPropagation();
        downloadSheet();
      }}
    >
      {downloadProgress ? (
        <Tooltip
          title={
            <div className={style.center}>
              หากค้างอยู่ที่หน้านี้นาน โปรดตรวจสอบแถบ Download ของ browser
              ของท่าน <br />
              โดยใน Safari จะอยู่ด้านขวาของ url bar เป็นลูกศรชี้ลง <br />
              ส่วนใน Chrome Mobile จะเด้งขึ้นมาด้านล่าง
            </div>
          }
        >
          <Spinner color={bgColor} />
        </Tooltip>
      ) : (
        <DocumentIcon color={bgColor} />
      )}{" "}
      {!isTutoring ? "อ่านชีท" : "อ่าน"}
    </button>
  );

  const renderTutoringBtn = () => {
    if (!isTutoring) return null;
    if (isExpired) {
      const hasReplayUrl = props.sheet?.onlineTutor?.liveReplayUrl != null;
      return (
        <Tooltip title={hasReplayUrl ? "ดูคลิปย้อนหลัง" : ""}>
          <span className="spanTooltip">
            <button
              className="tutoringBtn"
              style={{ color: bgColor, backgroundColor: bgButtonColor }}
              disabled={!hasReplayUrl}
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/myVideo/${props.sheet._id}`, "_blank");
              }}
            >
              <VideoOnlineIcon /> ดูย้อนหลัง {hasReplayUrl}
            </button>
          </span>
        </Tooltip>
      );
    }
    const hasUrlLive = props.sheet?.onlineTutor?.url != null;
    return (
      <Tooltip
        title={
          hasUrlLive
            ? ""
            : `เข้าร่วมได้เวลา ${formatTime(props?.sheet?.onlineTutor?.startDate)}`
        }
      >
        <span className="spanTooltip">
          <button
            className="tutoringBtn"
            style={{ color: bgColor, backgroundColor: bgButtonColor }}
            disabled={!hasUrlLive}
            onClick={(e) => {
              e.stopPropagation();
              if (hasUrlLive)
                window.open(props.sheet?.onlineTutor?.url, "_blank");
            }}
          >
            <AttendTheEvent color={bgColor} /> เข้าร่วมติว
          </button>
        </span>
      </Tooltip>
    );
  };

  // ── Bottom Action Section ──────────────────────────────────────────────────

  const renderBoughtActions = () => (
    <>
      <div className={style.authorSection} style={{ color: fontColor }}>
        {props?.sheet?.authorCode ?? "-"}
      </div>
      <div className={style.actionSection}>
        <div className={style.buttonGroup}>
          {renderReadSheetBtn()}
          {renderTutoringBtn()}
        </div>
        <Dropdown
          menu={{ items: dropdownItems }}
          trigger={["click"]}
          overlayClassName="overlayItemSheetCard"
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={style.tripleDot}
            onMouseEnter={() => setIsHoveredDropdown(true)}
            onMouseLeave={() => setIsHoveredDropdown(false)}
          >
            <TripleDotIcon color={isHoveredDropdown ? bgColor : "white"} />
          </div>
        </Dropdown>
      </div>
    </>
  );

  const renderCartBtn = () => (
    <button
      className={style.cartBtn}
      style={{ color: bgColor, backgroundColor: bgButtonColor }}
      onClick={async (e) => {
        e.stopPropagation();
        if (props?.addCart) {
          setProgress(true);
          const res = await props.addCart(props.sheet._id);
          if (res) setProgress(false);
        }
      }}
    >
      {progress ? (
        <Spinner color={bgColor} />
      ) : (
        <CartSheet style={{ color: bgColor }} />
      )}
      {props?.sheet?.price ?? "-"} บาท
    </button>
  );

  const renderDraftActions = () => (
    <div className={style.draftBtnContainer}>
      <button
        className={style.editBtn}
        onClick={() => props.callbackEditBtnClick?.()}
      >
        <img src={PenIcon} alt="pen-icon" /> แก้ไข
      </button>
      <button
        className={style.addToSystemBtn}
        style={{ color: darkenPropColor }}
        onClick={() => setTermAndConditionModal(true)}
      >
        <ExportFileIcon color={darkenPropColor} />
        ส่งเข้าระบบ
      </button>
    </div>
  );

  const renderNotBoughtActions = () => {
    const isDraft = props.isDraftSheet;
    if (isDraft && props.sheet?.status === 1) {
      return <></>;
    }
    return (
      <>
        <div className={style.actionSection}>
          <div className={style.authorSection} style={{ color: fontColor }}>
            {props?.sheet?.authorCode ?? "-"}
          </div>
        </div>
        {!props.hiddenCardButton &&
          (isDraft ? renderDraftActions() : renderCartBtn())}
      </>
    );
  };

  // ── Main Render ────────────────────────────────────────────────────────────

  const handleCardClick = () => {
    if (props.callbackClick) {
      props.callbackClick();
    } else if (!showReview && !showReport) {
      window.open(`/infoSheet/${props.sheet._id}`, "_blank");
    }
  };

  return (
    <div className={style.cardContainer} onClick={handleCardClick}>
      {/* Badge: ชีทแนะนำ */}
      {isSuggested && (
        <div className={style.suggestedLabel}>
          <img src={Suggested} alt="suggested" /> <b>ชีทแนะนำ!</b>
        </div>
      )}

      {/* Card Body */}
      <div
        className={[style.cardContent, isSuggested ? style.suggested : ""].join(
          " ",
        )}
        style={{
          width: props.size ?? "260px",
          backgroundColor: bgColor,
          cursor: props.isDraftSheet ? "default" : "pointer",
        }}
      >
        <Pattern
          color={isLight ? "#2F2F2F" : "#ffffff"}
          className={style.svgBackground}
        />

        <div className={style.contentContainer}>
          {/* Top Section: category / semester / bought count */}
          <div>
            {renderTopLabel()}
            <div ref={containerRef}>
              {renderCourseCode()}
              {renderCourseName()}
              {renderDraftStatusTag()}
            </div>
          </div>

          {/* Bottom Section: tutor tag + action buttons */}
          <div className={style.bottomLabel}>
            {renderOnlineTutorTag()}
            {props?.bought ? renderBoughtActions() : renderNotBoughtActions()}
          </div>
        </div>
      </div>

      {/* Modals */}
      <TermAndConditionSellerModal
        open={termAndConditionModal}
        loading={saving}
        onOk={() => {
          setTermAndConditionModal(false);
          confirmSend();
        }}
        onCancel={() => setTermAndConditionModal(false)}
      />

      <ShowReview
        showReview={showReview}
        review={{
          reviewID: review?.reviewID,
          reviewName: review?.reviewName,
          review: review?.review,
        }}
        onClose={() => setShowReview(false)}
      />

      <ShowReport
        showReport={showReport}
        reportID={report?.reportID}
        report={report?.report}
        reportName={report?.reportName}
        onClose={() => setShowReport(false)}
      />
    </div>
  );
};

export default SheetCard;
